import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Col, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import { connect } from "react-redux";
import numeral from "numeral";
import "numeral/locales";

const SummaryDonutChart = ({ useQuery, queryParams = null, selectedCurrency }) => {
  const { data, isFetching } = useQuery(queryParams);

  // Function to sanitize name for use as an HTML ID
  const sanitizeId = (name) => {
    return name
      ? `chart-item-${name.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-_]/g, "")}`
      : "chart-item";
  };

  const currencyMultiplier = (selectedCurrency?.value && !isNaN(selectedCurrency.value)) ? selectedCurrency.value : 1;

  // Function to format currency with shorthand and symbol
  const formatCurrencyShorthand = (value) => {
    if (!value || !selectedCurrency?.value) return '0';
    
    const convertedValue = Math.round(value * selectedCurrency.value);
    const locale = selectedCurrency.locale || 'no';
    
    // Set numeral locale
    numeral.locale(locale);
    
    // Get currency symbol
    const currencySymbols = {
      'NOK': 'kr',
      'SEK': 'kr', 
      'EUR': '€',
      'USD': '$',
      'GBP': '£'
    };
    
    const symbol = currencySymbols[selectedCurrency.currency] || selectedCurrency.currency;
    const formattedValue = numeral(convertedValue).format('0.[0]a');
    
    // For currencies that typically show symbol before (USD, EUR, GBP)
    if (['EUR', 'USD', 'GBP'].includes(selectedCurrency.currency)) {
      return `${symbol}${formattedValue}`;
    }
    
    // For currencies that show symbol after (NOK, SEK)
    return `${formattedValue} ${symbol}`;
  };

  const chartOptions = () => {
    return {
      credits: { enabled: false },
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        type: "pie",
        height: 300,
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: false,
          },
        },
      },
      title: {
        text: formatCurrencyShorthand((data?.total && !isNaN(data.total)) ? data.total : 0),
        verticalAlign: "middle",
        y: 25,
        style: {
          fontSize: "32px",
          color: "white",
        },
      },
      subtitle: {
        text: `${data?.trend >= 0 ? "+" : ""}${data?.trend}%`,
        verticalAlign: "middle",
        y: 45,
        style: {
          fontSize: "16px",
          color: "var(--text-secondary)",
        },
      },
      yAxis: {
        title: {
          text: "",
        },
      },
      xAxis: {},
      series: [
        {
          name: "Verdi",
          innerSize: "75%",
          borderColor: "transparent",
          data: data?.series?.map(item => ({
            ...item,
            y: (item.y && !isNaN(item.y)) ? Math.round(item.y * currencyMultiplier) : 0
          })) ?? [],
        },
      ],
    };
  };
  return (
    <div style={{ position: "relative" }}>
      {isFetching && (
        <div style={{ 
          position: "absolute", 
          top: "50%", 
          left: "50%", 
          transform: "translate(-50%, -50%)",
          zIndex: 10
        }}>
          <Spinner color="primary" />
        </div>
      )}
      <Row style={{ flexWrap: "wrap-reverse", opacity: isFetching ? 0.5 : 1, transition: "opacity 0.3s" }} className="px-4">
        <Col xs={12} sm={12} md={12} lg={8} xl={6}>
          <div className="flex flex-column justify-content-center h-100">
            <table>
              <tbody>
                {data?.series?.map?.((item, index) => {
                  const itemId = sanitizeId(item.name);
                  return (
                    <tr key={index}>
                      <td
                        className="flex align-items-center mt-2 mb-2 mr-4"
                        id={itemId}
                      >
                        <div
                          style={{
                            backgroundColor: item.color,
                            width: 42,
                            height: 32,
                            borderRadius: 5,
                          }}
                          className="mr-3"
                        />

                        <p className="m-0">{item.name}</p>
                      </td>
                      <td className="mr-4">{formatCurrencyShorthand((item.y && !isNaN(item.y)) ? item.y : 0)}</td>
                      <td
                        className={
                          item.trend >= 0
                            ? "positive-number ml-4"
                            : "negative-number ml-4"
                        }
                      >
                        {item.trend >= 0 ? "+" : ""}
                        {item.trend}%
                      </td>
                      {item?.tooltip && (
                        <UncontrolledTooltip placement="top" target={itemId}>
                          {item.tooltip}
                        </UncontrolledTooltip>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={6}>
          <HighchartsReact highcharts={Highcharts} options={chartOptions()} />
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = state => ({
  selectedCurrency: state.currency.selectedCurrency,
});

export default connect(mapStateToProps)(SummaryDonutChart);
